import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Campaign as CampaignIcon, Search } from "@mui/icons-material";
import { useCampaigns } from "@/hooks/useCampaigns";
import { CAMPAIGN_STATUS_COLORS, CAMPAIGN_STATUS_LABELS, type CampaignStatus } from "@/types";

export default function Campaigns() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<CampaignStatus | "">("");
  const [search, setSearch] = useState("");

  const {
    data: campaigns,
    isLoading,
    error,
  } = useCampaigns({
    status: statusFilter,
    search: search || undefined,
  });

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <CampaignIcon color="action" />
        <Typography variant="h4" sx={{ flex: 1 }}>
          Campaigns
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate("/campaigns/new")}>
          New Campaign
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Search campaigns…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} /> }}
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value as CampaignStatus | "")}
          >
            <MenuItem value="">All</MenuItem>
            {Object.entries(CAMPAIGN_STATUS_LABELS).map(([k, v]) => (
              <MenuItem key={k} value={k}>
                {v}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">Failed to load campaigns.</Alert>}

      {campaigns && campaigns.length === 0 && (
        <Alert severity="info">
          No campaigns yet. Click "New Campaign" to plan your first one.
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {campaigns?.map((c) => (
          <Card key={c.id} sx={{ "&:hover": { boxShadow: 3 } }}>
            <CardActionArea onClick={() => navigate(`/campaigns/${c.id}`)}>
              <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.5 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {c.name}
                  </Typography>
                  {c.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {c.description}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {c.target_segment && `${c.target_segment} · `}
                    {c.start_date && `From ${c.start_date}`}
                    {c.end_date && ` to ${c.end_date}`}
                    {c.owner_agent && ` · Owner: ${c.owner_agent}`}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {c.tags?.map((t) => (
                    <Chip key={t} label={t} size="small" variant="outlined" />
                  ))}
                </Box>
                <Chip
                  label={CAMPAIGN_STATUS_LABELS[c.status]}
                  size="small"
                  color={CAMPAIGN_STATUS_COLORS[c.status]}
                />
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  );
}
